import React, { useCallback, useMemo } from 'react';
import { Accordion, Button, Highlight, Text } from '@mantine/core';
import { IconUserPlus } from '@tabler/icons-react';

import { useOrganisationSearch } from '../../context/OrganisationSearchContext';
import { OpsDivision } from '../../codegen/graphql';
import Stores from './Stores';
import { PropagationStopper } from '../common';
import stringifyNumber from '../../utility/stringifyNumber';
import { useAuth } from '../../context/AuthProvider';
import { useUserPortal } from '../../context/UserPortalContext';

type Props = Readonly<{
    division: OpsDivision;
}>;

function DivisionCard({ division }: Props) {
    const { role } = useAuth();
    const { organisationSearch } = useOrganisationSearch();
    const { openUserSidesheet } = useUserPortal();
    const stores = useMemo(() => {
        if (!division.stores || !division.stores.length) {
            return [];
        }

        return division.stores;
    }, [division]);

    const divisionManager = useMemo(() => {
        if (division.manager) {
            const fullName = `${division.manager.firstName} ${division.manager.lastName}`;
            return { ...division.manager, fullName };
        }
        return {
            fullName: '',
            designation: '',
            speedDial: '',
            telNo: {
                dialingCode: '',
                number: '',
                countryCode: '',
            },
            mobileNo: {
                dialingCode: '',
                number: '',
                countryCode: '',
            },
            email: '',
        };
    }, [division]);

    const openAddStoreManagerModal = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            openUserSidesheet();
        },
        [openUserSidesheet],
    );

    const divisionManagerNumber = useMemo(() => {
        const number = divisionManager.telNo || divisionManager.mobileNo;

        if (!number) {
            return null;
        }

        return stringifyNumber(number);
    }, []);

    return (
        <Accordion.Item value={division.id ?? ''}>
            <Accordion.Control>
                <div className="grid grid-cols-24 items-center h-[64px]">
                    <div className="border-solid border-l-3 border-primary border-0 h-full flex justify-between col-span-4 p-2">
                        <div className="flex flex-col gap-1 overflow-hidden justify-center">
                            <Highlight
                                fw="bold"
                                tt="uppercase"
                                highlight={organisationSearch}
                            >
                                {division.name ?? ''}
                            </Highlight>
                            {divisionManager.fullName ? (
                                <Text className="text-sm whitespace-nowrap text-ellipsis max-w-full">
                                    <Highlight highlight={organisationSearch}>
                                        {`DM - ${divisionManager.fullName}`}
                                    </Highlight>
                                </Text>
                            ) : (
                                <Text
                                    color="#e06565"
                                    className="text-sm whitespace-nowrap text-ellipsis max-w-full"
                                >
                                    No associated DM
                                </Text>
                            )}
                        </div>
                    </div>

                    <div className="flex col-span-1 p-2">
                        <Text className="text-sm">
                            <Highlight highlight={organisationSearch}>
                                {divisionManager.speedDial || 'N/A'}
                            </Highlight>
                        </Text>
                    </div>

                    <div className="flex col-span-3 p-2 overflow-hidden">
                        {divisionManagerNumber ? (
                            <PropagationStopper>
                                <a
                                    href={`tel:${divisionManagerNumber}`}
                                    className="hover:underline whitespace-nowrap text-ellipsis overflow-hidden text-sm text-primary"
                                >
                                    <Highlight highlight={organisationSearch}>
                                        {divisionManagerNumber}
                                    </Highlight>
                                </a>
                            </PropagationStopper>
                        ) : (
                            <p className="whitespace-nowrap text-ellipsis overflow-hidden text-sm">
                                N/A
                            </p>
                        )}
                    </div>

                    <div className="flex col-span-5 p-2 overflow-hidden">
                        {divisionManager.email ? (
                            <PropagationStopper>
                                <a
                                    href={`mailto:${divisionManager.email}`}
                                    type="email"
                                    className="hover:underline whitespace-nowrap text-ellipsis overflow-hidden text-sm text-primary"
                                >
                                    <Highlight highlight={organisationSearch}>
                                        {divisionManager.email}
                                    </Highlight>
                                </a>
                            </PropagationStopper>
                        ) : (
                            <p className="whitespace-nowrap text-ellipsis overflow-hidden text-sm">
                                N/A
                            </p>
                        )}
                    </div>

                    <div className="col-span-5 p-2">
                        <Text className="text-sm">
                            <Highlight highlight={organisationSearch}>
                                {division.regionalLocation || 'N/A'}
                            </Highlight>
                        </Text>
                    </div>

                    <div className="col-span-1" />

                    <div className="col-span-4">
                        {(role === 'admin' || role === 'superadmin') && (
                            <Button
                                variant="subtle"
                                className="text-primary rounded-md p-1 w-min h-min ml-auto"
                                onClick={openAddStoreManagerModal}
                            >
                                <IconUserPlus />
                            </Button>
                        )}
                    </div>
                </div>
            </Accordion.Control>
            <Accordion.Panel>
                <Stores stores={stores} division={division} />
            </Accordion.Panel>
        </Accordion.Item>
    );
}

export default DivisionCard;
